// Adaptive learning system with spaced repetition
import type { LearningInsight, PracticeSession, PracticeForm } from "@/types";
import type { StatsTracker } from "@/statistics/stats-tracker";
import type { GamificationSystem } from "@/statistics/gamification";

export class LearningSystem {
  private statsTracker: StatsTracker;
  // @ts-ignore TS6133: Reserved for future use
  private gamificationSystem: GamificationSystem;

  constructor(
    statsTracker: StatsTracker,
    gamificationSystem: GamificationSystem
  ) {
    this.statsTracker = statsTracker;
    this.gamificationSystem = gamificationSystem;
  }

  /**
   * Calculate spaced repetition weight for a form
   */
  calculateSpacedRepetitionWeight(tense: string, pronoun: string): number {
    const stats = this.statsTracker.getFormStats(tense, pronoun);
    const now = Date.now();

    // Base weight starts at 1.0
    let weight = 1.0;

    // Factor 1: Accuracy-based weighting
    if (stats.total > 0) {
      if (stats.accuracy < 50) {
        weight *= 3.0; // Heavy emphasis on struggling areas
      } else if (stats.accuracy < 70) {
        weight *= 2.0; // Medium emphasis
      } else if (stats.accuracy < 90) {
        weight *= 1.2; // Light emphasis
      } else {
        weight *= 0.5; // Reduced emphasis on mastered areas
      }
    } else {
      weight *= 2.5; // New forms get high priority
    }

    // Factor 2: Time-based spaced repetition
    const lastPracticed = this.statsTracker.getLastPracticed(tense, pronoun);
    if (lastPracticed) {
      const hoursSinceLastPractice = (now - lastPracticed) / (1000 * 60 * 60);

      // Optimal spacing based on accuracy
      let optimalHours: number;
      if (stats.accuracy >= 90) {
        optimalHours = 72; // 3 days for mastered
      } else if (stats.accuracy >= 70) {
        optimalHours = 24; // 1 day for good
      } else {
        optimalHours = 8; // 8 hours for struggling
      }

      // Increase weight as we approach or exceed optimal spacing
      const spacingMultiplier = Math.min(
        2.0,
        hoursSinceLastPractice / optimalHours
      );
      weight *= 0.5 + spacingMultiplier;
    } else {
      weight *= 1.5; // Never practiced gets bonus
    }

    // Factor 3: Recent trend analysis
    const trend = this.statsTracker.getAccuracyTrend(tense, pronoun);
    if (trend < -10) {
      weight *= 1.5; // Declining performance needs attention
    } else if (trend > 10) {
      weight *= 0.8; // Improving performance needs less focus
    }

    // Factor 4: Challenge level consideration
    const challengeLevel = this.getChallengeLevel(tense, pronoun);
    if (challengeLevel === 1 && stats.total > 5) {
      weight *= 1.3; // Encourage progression from multiple choice
    }

    return Math.max(0.1, Math.min(5.0, weight)); // Clamp between 0.1 and 5.0
  }

  /**
   * Get learning insights for the user
   */
  getLearningInsights(): LearningInsight[] {
    const insights: LearningInsight[] = [];
    const detailedStats = this.statsTracker.getDetailedStats();

    // Struggling forms analysis
    const strugglingForms: Array<{
      form: string;
      reason: string;
      priority: "urgent" | "high" | "normal";
    }> = [];
    const masteredForms: Array<{
      form: string;
      reason: string;
      priority: "urgent" | "high" | "normal";
    }> = [];
    const staleForms: Array<{
      form: string;
      reason: string;
      priority: "urgent" | "high" | "normal";
    }> = [];

    for (const [tense, pronounStats] of Object.entries(detailedStats)) {
      for (const [pronoun, stats] of Object.entries(pronounStats)) {
        if (stats.total > 0) {
          const formDisplay = `${(window as any).verbData.getTenseName(
            tense
          )} - ${pronoun}`;

          if (stats.accuracy < 50 && stats.total >= 3) {
            strugglingForms.push({
              form: formDisplay,
              reason: `Only ${stats.accuracy}% accuracy after ${stats.total} attempts`,
              priority: "urgent",
            });
          } else if (stats.accuracy >= 90 && stats.total >= 5) {
            masteredForms.push({
              form: formDisplay,
              reason: `${stats.accuracy}% accuracy - well mastered!`,
              priority: "normal",
            });
          }

          // Check for stale forms (not practiced recently)
          const lastPracticed = this.statsTracker.getLastPracticed(
            tense,
            pronoun
          );
          if (lastPracticed) {
            const daysSince =
              (Date.now() - lastPracticed) / (1000 * 60 * 60 * 24);
            if (daysSince > 7 && stats.accuracy < 80) {
              staleForms.push({
                form: formDisplay,
                reason: `Not practiced for ${Math.round(daysSince)} days`,
                priority: "high",
              });
            }
          }
        }
      }
    }

    // Add insights
    if (strugglingForms.length > 0) {
      insights.push({
        type: "struggling",
        title: "🎯 Forms That Need Extra Practice",
        items: strugglingForms.slice(0, 5),
      });
    }

    if (staleForms.length > 0) {
      insights.push({
        type: "stale",
        title: "⏰ Forms Due for Review",
        items: staleForms.slice(0, 5),
      });
    }

    if (masteredForms.length > 0) {
      insights.push({
        type: "mastered",
        title: "🌟 Well-Mastered Forms",
        items: masteredForms.slice(0, 3),
      });
    }

    return insights;
  }

  /**
   * Suggest optimal practice session
   */
  suggestPracticeSession(): PracticeSession {
    const allForms = (window as any).verbData.getAllForms("all");
    const prioritizedForms: PracticeForm[] = allForms
      .map((form: any) => ({
        ...form,
        weight: this.calculateSpacedRepetitionWeight(form.tense, form.pronoun),
      }))
      .sort((a: PracticeForm, b: PracticeForm) => b.weight - a.weight)
      .slice(0, 10);

    return {
      recommendedForms: prioritizedForms,
      estimatedTime: Math.min(20, prioritizedForms.length * 2), // 2 minutes per form, max 20 min
      focus: this.determineFocus(prioritizedForms),
    };
  }

  /**
   * Determine the focus area for practice
   */
  determineFocus(forms: PracticeForm[]): string {
    const tenseCount: Record<string, number> = {};
    forms.forEach((form) => {
      tenseCount[form.tense] = (tenseCount[form.tense] || 0) + 1;
    });

    const topTense = Object.entries(tenseCount).sort(
      ([, a], [, b]) => b - a
    )[0]?.[0];

    if (topTense) {
      return `Focus on ${(window as any).verbData.getTenseName(
        topTense
      )} forms`;
    }

    return "Mixed practice across all forms";
  }

  /**
   * Calculate learning velocity (forms mastered per day)
   */
  calculateLearningVelocity(): number {
    const daysSinceStart = this.statsTracker.getDaysSinceFirstPractice();

    if (daysSinceStart === 0) return 0;

    const masteredForms = this.statsTracker.getMasteredFormsCount();
    return masteredForms / daysSinceStart;
  }

  /**
   * Predict time to mastery for all forms
   */
  predictTimeToMastery(): string {
    const totalForms = (window as any).verbData.getAllForms("all").length;
    const masteredForms = this.statsTracker.getMasteredFormsCount();
    const remainingForms = totalForms - masteredForms;

    const velocity = this.calculateLearningVelocity();

    if (velocity === 0) {
      return "Continue practicing to get an estimate";
    }

    const daysToMastery = Math.ceil(remainingForms / velocity);

    if (daysToMastery <= 7) {
      return `About ${daysToMastery} days`;
    } else if (daysToMastery <= 30) {
      return `About ${Math.ceil(daysToMastery / 7)} weeks`;
    } else {
      return `About ${Math.ceil(daysToMastery / 30)} months`;
    }
  }

  /**
   * Calculate challenge level for a form
   */
  getChallengeLevel(tense: string, pronoun: string): number {
    const stats = this.statsTracker.getFormStats(tense, pronoun);
    if (stats.total === 0) return 1; // Multiple choice for new
    if (stats.accuracy >= 80 && stats.total >= 5) return 3; // Translation for mastered
    return 2; // Fill-blank for intermediate
  }
}
